import React, { useState } from 'react';
import { Save, Clock, Users, Timer, Download, Upload, Database, Trash2, Palette, Moon, Sun, Plus, Image, Eye, EyeOff, Cpu, Sparkles, HelpCircle, Key, RefreshCw, Search, CheckCircle, Copy, Edit2, Check, X, AlertTriangle } from 'lucide-react';
import { AIFailureLog, AppConfig } from '../types';
import { updateConfig as updateConfigApi, exportCompleteConfig, importCompleteConfig, cleanupOrphanedFiles, optimizeExistingMedia, getAIFailureLogs } from '../services/api';
import { changePassword, getCurrentUser } from '../services/authApi';
import { GlobalSessionToggle } from './GlobalSessionToggle';
import { useGlobalSessions } from '../hooks/useGlobalSessions';
import { useSession } from '../context/SessionContext';
import { ConfirmModal } from './ConfirmModal';
import { ApiIntegrationSettings } from './ApiIntegrationSettings';
import { THEME_COLORS, applyTheme, applyCustomTheme, toggleDarkMode } from '../services/themeService';

interface SettingsProps {
  config: AppConfig;
  setConfig: (config: AppConfig) => void;
  toast?: {
    success: (message: string) => void;
    error: (message: string) => void;
    warning: (message: string) => void;
    info: (message: string) => void;
  };
}

export const Settings: React.FC<SettingsProps> = ({ config, setConfig, toast }) => {
  const [localConfig, setLocalConfig] = useState<AppConfig>(config);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Synchronize localConfig with prop changes
  React.useEffect(() => {
    setLocalConfig(config);
    setOpenaiKeys(splitKeys(config.openaiApiKey || ''));
    setGoogleKeys(splitKeys(config.googleApiKey || ''));
    setAnthropicKeys(splitKeys(config.anthropicApiKey || ''));
    setXaiKeys(splitKeys(config.xaiApiKey || ''));
  }, [config]);
  const currentUser = getCurrentUser();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const { globalSessionsEnabled, setGlobalSessionsEnabled } = useGlobalSessions();
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const { selectedSessionId, sessions } = useSession();
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [cleanupAllSessions, setCleanupAllSessions] = useState(false);
  const [aiFailures, setAIFailures] = useState<AIFailureLog[]>([]);
  const [loadingAIFailures, setLoadingAIFailures] = useState(false);

  // States for AI API visibility and Failover info
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>({});

  // Multiple API Keys States per Provider
  const [openaiKeys, setOpenaiKeys] = useState<string[]>(['']);
  const [googleKeys, setGoogleKeys] = useState<string[]>(['']);
  const [anthropicKeys, setAnthropicKeys] = useState<string[]>(['']);
  const [xaiKeys, setXaiKeys] = useState<string[]>(['']);

  const loadAIFailureLogs = async () => {
    try {
      setLoadingAIFailures(true);
      const response = await getAIFailureLogs(100);
      if (response?.success) {
        setAIFailures(Array.isArray(response.logs) ? response.logs : []);
      }
    } catch (error) {
      console.error('Error cargando fallos de IA:', error);
      toast?.error('No se pudo cargar el registro de fallos de IA');
    } finally {
      setLoadingAIFailures(false);
    }
  };

  React.useEffect(() => {
    void loadAIFailureLogs();
  }, []);

  const formatAIFailureDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const splitKeys = (keyStr: string) => {
    const list = (keyStr || '').split(',').map(s => s.trim()).filter(Boolean);
    return list.length > 0 ? list : [''];
  };

  const renderKeyListInputs = (
    providerLabel: string,
    keysList: string[],
    setKeysList: React.Dispatch<React.SetStateAction<string[]>>,
    providerKey: string,
    obtainKeyUrl: string,
    placeholder: string,
    modelsHelp: React.ReactNode
  ) => {
    const handleKeyChange = (index: number, val: string) => {
      const updated = [...keysList];
      updated[index] = val;
      setKeysList(updated);
    };

    const addKeyField = () => {
      setKeysList([...keysList, '']);
    };

    const removeKeyField = (index: number) => {
      const updated = keysList.filter((_, i) => i !== index);
      setKeysList(updated.length > 0 ? updated : ['']);
    };

    return (
      <div className="border border-theme rounded-xl p-4 bg-theme-base/30 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm font-bold text-theme-main flex items-center gap-2">
            {providerLabel}
          </span>
          {obtainKeyUrl && (
            <a href={obtainKeyUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline flex items-center gap-1">
              Obtener Key <HelpCircle size={12} />
            </a>
          )}
        </div>
        
        <div className="space-y-2">
          {keysList.map((keyVal, idx) => {
            const visibilityId = `${providerKey}_${idx}`;
            return (
              <div key={idx} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    type={visibleKeys[visibilityId] ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder={placeholder + (idx > 0 ? ` (Llave alternativa ${idx + 1})` : '')}
                    className="w-full pl-3 pr-10 py-2 border border-theme rounded-lg text-xs font-mono bg-theme-card"
                    value={keyVal}
                    onChange={(e) => handleKeyChange(idx, e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => toggleKeyVisibility(visibilityId)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-muted hover:text-theme-main"
                  >
                    {visibleKeys[visibilityId] ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {keysList.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeKeyField(idx)}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors shrink-0"
                    title="Eliminar llave"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-between items-center pt-1">
          <button
            type="button"
            onClick={addKeyField}
            className="text-xs text-primary-600 hover:text-primary-500 font-semibold flex items-center gap-1 transition-colors"
          >
            <Plus size={14} /> Agregar otra clave API
          </button>
          {modelsHelp && (
            <div className="text-[10px] text-theme-muted text-right">
              {modelsHelp}
            </div>
          )}
        </div>
      </div>
    );
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast?.success(`¡${label} copiado al portapapeles!`);
  };

  const toggleKeyVisibility = (key: string) => {
    setVisibleKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Theme state
  const [activeTheme, setActiveTheme] = useState(() => localStorage.getItem('theme_color') || 'green');
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme_mode') === 'dark');

  const handleThemeChange = (color: string) => {
    setActiveTheme(color);
    applyTheme(color);
  };

  const handleModeChange = (isDark: boolean) => {
    setIsDarkMode(isDark);
    toggleDarkMode(isDark);
  };

  const handleCustomColorChange = (hex: string) => {
    setActiveTheme('custom');
    applyCustomTheme(hex);
  };

  const handleExportComplete = async () => {
    try {
      setIsExporting(true);
      await exportCompleteConfig();
      toast?.success('Configuración completa exportada (menús, reglas y archivos multimedia)');
    } catch (error) {
      console.error('Error exporting complete config:', error);
      toast?.error('Error al exportar configuración completa');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportComplete = async () => {
    if (!importFile) {
      toast?.warning('Por favor selecciona un archivo ZIP');
      return;
    }

    try {
      setIsImporting(true);
      const result = await importCompleteConfig(importFile, globalSessionsEnabled);

      const scope = globalSessionsEnabled ? 'todas las sesiones' : 'sesión actual';
      const menusMsg = result.menus.imported > 0 ? `${result.menus.imported} menús importados` : '';
      const menusReplacedMsg = result.menus.replaced > 0 ? `${result.menus.replaced} menús actualizados` : '';
      const rulesMsg = result.rules.imported > 0 ? `${result.rules.imported} reglas importadas` : '';
      const rulesReplacedMsg = result.rules.replaced > 0 ? `${result.rules.replaced} reglas actualizadas` : '';
      const mediaMsg = result.media > 0 ? `${result.media} archivos multimedia` : '';

      const parts = [menusMsg, menusReplacedMsg, rulesMsg, rulesReplacedMsg, mediaMsg].filter(Boolean);
      const message = `Importación completa exitosa (${scope}): ${parts.join(', ')}`;

      toast?.success(message);
      setImportFile(null);

      // Reload page to refresh data
      setTimeout(() => window.location.reload(), 1500);
    } catch (error: any) {
      console.error('Error importing complete config:', error);
      toast?.error(error.message || 'Error al importar configuración completa');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCleanupOrphanedFiles = async (allSessions: boolean = false) => {
    try {
      setIsCleaning(true);
      const result = await cleanupOrphanedFiles(
        allSessions ? undefined : selectedSessionId || undefined,
        allSessions
      );

      const scope = allSessions ? 'todas las sesiones' : 'sesión actual';
      if (result.deletedCount > 0) {
        toast?.success(`${result.deletedCount} archivo(s) huérfano(s) eliminado(s) de ${scope}`);
      } else {
        toast?.info(`No se encontraron archivos huérfanos en ${scope}`);
      }
    } catch (error: any) {
      console.error('Error cleaning orphaned files:', error);
      toast?.error(error?.message || 'Error al limpiar archivos huérfanos');
    } finally {
      setIsCleaning(false);
    }
  };

  const handleCleanupClick = (allSessions: boolean) => {
    setCleanupAllSessions(allSessions);
    setShowCleanupConfirm(true);
  };

  const confirmCleanup = () => {
    handleCleanupOrphanedFiles(cleanupAllSessions);
  };

  const handleOptimizeExistingMedia = async () => {
    try {
      setIsOptimizing(true);
      const result = await optimizeExistingMedia();
      if (result.success && result.optimizedCount > 0) {
        const savings = result.spaceSavedPercent ? `${result.spaceSavedPercent}%` : '0%';
        const initialSizeMb = (result.initialSizeTotal / (1024 * 1024)).toFixed(2);
        const finalSizeMb = (result.finalSizeTotal / (1024 * 1024)).toFixed(2);
        toast?.success(`Optimización exitosa: ${result.optimizedCount} imágenes optimizadas. Reducción de ${initialSizeMb} MB a ${finalSizeMb} MB (¡Ahorro de ${savings}!).`);
      } else {
        toast?.info('No se encontraron imágenes aptas para optimizar o todas ya estaban optimizadas.');
      }
    } catch (error: any) {
      console.error('Error optimizing existing media:', error);
      toast?.error(error?.message || 'Error al optimizar imágenes en el servidor');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleSave = async () => {
    const { 
      messageDelay, 
      maxContactsPerBatch, 
      waitTimeBetweenBatches, 
      defaultCountryCode, 
      autoReplyInGroups,
      pauseOnHumanReply,
      pauseDurationMin,
      messageGroupingDelaySeconds,
      aiFailoverEnabled,
      integrationProvider,
      integrationSessions,
      integrationFallbackSession
    } = localConfig;

    if (!messageDelay || messageDelay <= 0) {
      toast?.error('El retraso entre mensajes debe ser un número mayor a 0.');
      setSaveStatus('error');
      return;
    }

    if (!maxContactsPerBatch || maxContactsPerBatch <= 0) {
      toast?.error('El máximo de contactos por lote debe ser un número mayor a 0.');
      setSaveStatus('error');
      return;
    }

    if (waitTimeBetweenBatches === undefined || waitTimeBetweenBatches < 0) {
      toast?.error('El tiempo de espera entre lotes debe ser un número igual o mayor a 0.');
      setSaveStatus('error');
      return;
    }

    if (!Number.isInteger(messageGroupingDelaySeconds) || messageGroupingDelaySeconds! < 1 || messageGroupingDelaySeconds! > 120) {
      toast?.error('El tiempo para agrupar mensajes debe estar entre 1 y 120 segundos.');
      setSaveStatus('error');
      return;
    }

    const normalizedConfig: AppConfig = {
      messageDelay,
      maxContactsPerBatch,
      waitTimeBetweenBatches,
      defaultCountryCode: (defaultCountryCode || '').trim(),
      autoReplyInGroups: autoReplyInGroups || false,
      pauseOnHumanReply: pauseOnHumanReply !== undefined ? pauseOnHumanReply : true,
      pauseDurationMin: pauseDurationMin !== undefined ? pauseDurationMin : 30,
      messageGroupingDelaySeconds: messageGroupingDelaySeconds!,
      openaiApiKey: openaiKeys.map(s => s.trim()).filter(Boolean).join(','),
      anthropicApiKey: anthropicKeys.map(s => s.trim()).filter(Boolean).join(','),
      xaiApiKey: xaiKeys.map(s => s.trim()).filter(Boolean).join(','),
      googleApiKey: googleKeys.map(s => s.trim()).filter(Boolean).join(','),
      aiFailoverEnabled: aiFailoverEnabled || false,
      integrationProvider: integrationProvider || '',
      integrationSessions: integrationSessions || [],
      integrationFallbackSession: integrationFallbackSession || ''
    };

    try {
      const response = await updateConfigApi(normalizedConfig);
      if (!response?.success) {
        throw new Error('No se pudo guardar la configuración');
      }
      const savedConfig = response.config || normalizedConfig;
      setConfig(savedConfig);
      setLocalConfig(savedConfig);
      setSaveStatus('success');
      toast?.success('¡Configuración guardada exitosamente!');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      console.error('Error saving config:', err);
      setSaveStatus('error');
      toast?.error(err?.message || 'No se pudo guardar la configuración');
    }
  };

  const handleChangePassword = async () => {
    if (!currentUser?.email) {
      toast?.error('No se pudo obtener el usuario actual. Inicia sesión nuevamente.');
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast?.error('Completa todos los campos de contraseña.');
      return;
    }

    if (newPassword.length < 6) {
      toast?.error('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast?.error('La nueva contraseña y la confirmación no coinciden.');
      return;
    }

    try {
      setChangingPassword(true);
      const response = await changePassword(currentUser.email, currentPassword, newPassword);
      if (!response?.success) {
        throw new Error(response?.message || 'No se pudo cambiar la contraseña');
      }

      toast?.success(response.message || 'Contraseña actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      console.error('Error changing password:', err);
      // Intentar mostrar mensaje de error del backend si existe
      const backendMessage = err?.response?.data?.error || err?.message;
      toast?.error(backendMessage || 'No se pudo cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      {saveStatus === 'success' && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
          <p className="text-sm text-primary-600">¡Configuración guardada exitosamente!</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Apariencia */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
          <h3 className="text-lg font-bold text-theme-main mb-6 flex items-center gap-2">
            <Palette size={20} className="text-pink-500" /> Apariencia
          </h3>

          <div className="space-y-6">
            {/* Mode Selector */}
            <div>
              <label className="block text-sm font-medium text-theme-main mb-3">Modo</label>
              <div className="flex gap-4">
                <button
                  onClick={() => handleModeChange(false)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border transition-all ${!isDarkMode ? 'bg-slate-800 text-white border-theme' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-base'}`}
                >
                  <Sun size={18} /> Claro
                </button>
                <button
                  onClick={() => handleModeChange(true)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border transition-all ${isDarkMode ? 'bg-slate-800 text-white border-theme' : 'bg-theme-card text-theme-muted border-theme hover:bg-theme-base'}`}
                >
                  <Moon size={18} /> Oscuro
                </button>
              </div>
            </div>

            {/* Color Palette */}
            <div>
              <label className="block text-sm font-medium text-theme-main mb-3">Color de énfasis</label>
              <div className="flex flex-wrap gap-3">
                {THEME_COLORS.map((theme) => {
                  // Use the 500 value from variables to show preview
                  // We have to extract RGB string "R G B" and put in style
                  const rgb = theme.variables['--primary-500'];
                  return (
                    <button
                      key={theme.value}
                      onClick={() => handleThemeChange(theme.value)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${activeTheme === theme.value ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : 'hover:scale-105'}`}
                      style={{ backgroundColor: `rgb(${rgb})` }}
                      title={theme.name}
                    >
                      {activeTheme === theme.value && (
                        <div className="w-2.5 h-2.5 bg-theme-card rounded-full shadow-sm" />
                      )}
                    </button>
                  );
                })}

                {/* Custom Color Picker */}
                <div className="relative group">
                  <div className={`w-10 h-10 rounded-full overflow-hidden flex items-center justify-center transition-all cursor-pointer bg-gradient-to-br from-red-500 via-green-500 to-blue-500 ${activeTheme === 'custom' ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : 'hover:scale-105'}`}>
                    <input
                      type="color"
                      className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                      title="Color Personalizado"
                      onChange={(e) => {
                        handleCustomColorChange(e.target.value);
                      }}
                      value={activeTheme === 'custom' ? (localStorage.getItem('theme_custom_hex') || '#000000') : '#ffffff'}
                    />
                    {activeTheme === 'custom' ? (
                      <div className="w-2.5 h-2.5 bg-white rounded-full shadow-sm pointer-events-none" />
                    ) : (
                      <Plus size={20} className="text-white pointer-events-none" />
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs text-theme-muted mt-2">
                Selecciona el color principal para la interfaz.
              </p>
            </div>
          </div>
        </div>

        {/* Auto-Reply Configuration */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
          <h3 className="text-lg font-bold text-theme-main mb-6 flex items-center gap-2">
            <Users size={20} className="text-blue-500" /> Configuración de Auto-Respuestas
          </h3>

          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-theme-base rounded-lg border border-theme">
              <div className="flex items-center gap-3">
                <Users size={20} className="text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-theme-main">Auto Responder en Grupos</p>
                  <p className="text-xs text-theme-muted">Permitir respuestas automáticas en chats grupales</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localConfig.autoReplyInGroups || false}
                  onChange={(e) => setLocalConfig({ ...localConfig, autoReplyInGroups: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-theme-card after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-theme-base rounded-lg border border-theme">
              <div className="flex items-center gap-3">
                <Users size={20} className="text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-theme-main">Pausar Bot si Humano Responde</p>
                  <p className="text-xs text-theme-muted">Pausar bot temporalmente cuando respondes manualmente</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localConfig.pauseOnHumanReply ?? true}
                  onChange={(e) => setLocalConfig({ ...localConfig, pauseOnHumanReply: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-theme-card after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="p-4 bg-theme-base rounded-lg border border-theme space-y-2">
              <label className="block text-sm font-medium text-theme-main">
                Tiempo de Pausa del Bot
              </label>
              <select
                className="w-full px-3 py-2 border border-theme rounded-lg text-sm bg-theme-card text-theme-main"
                value={localConfig.pauseDurationMin ?? 30}
                onChange={(e) => setLocalConfig({ ...localConfig, pauseDurationMin: parseInt(e.target.value) })}
                disabled={localConfig.pauseOnHumanReply === false}
              >
                <option value={15}>15 minutos</option>
                <option value={30}>30 minutos</option>
                <option value={60}>1 hora</option>
                <option value={120}>2 horas</option>
                <option value={720}>12 horas</option>
                <option value={1440}>24 horas</option>
                <option value={-1}>Permanente</option>
              </select>
              <p className="text-xs text-theme-muted">
                Cuánto tiempo se silenciará el chatbot tras una interacción manual
              </p>
            </div>

            <div className="p-4 bg-theme-base rounded-lg border border-theme space-y-2">
              <label htmlFor="message-grouping-delay" className="block text-sm font-medium text-theme-main">
                Espera para agrupar mensajes (segundos)
              </label>
              <input
                id="message-grouping-delay"
                type="number"
                min={1}
                max={120}
                step={1}
                autoComplete="off"
                value={localConfig.messageGroupingDelaySeconds ?? 5}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10);
                  setLocalConfig({ ...localConfig, messageGroupingDelaySeconds: value });
                }}
                className="w-full px-3 py-2 border border-theme rounded-lg text-sm bg-theme-card text-theme-main"
              />
              <p className="text-xs text-theme-muted">
                Después del último mensaje, el bot espera este tiempo antes de responder. Cada mensaje nuevo reinicia la espera; así, una secuencia como “Hola”, “qué tal” y “quiero inscribirme al curso” llega en una sola respuesta.
              </p>
            </div>

            {/* Global Sessions Toggle */}
            <GlobalSessionToggle
              enabled={globalSessionsEnabled}
              onChange={setGlobalSessionsEnabled}
              label="Menús y Auto-Respuestas Globales"
              description="Cuando está activado, los menús interactivos y auto-respuestas funcionarán en todas las sesiones de WhatsApp conectadas. Cuando está desactivado, solo funcionarán en la sesión actualmente seleccionada."
            />
          </div>
        </div>

        {/* Envío individual */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
          <h3 className="text-lg font-bold text-theme-main mb-6 flex items-center gap-2">
            <Users size={20} className="text-primary-500" /> Envío Individual
          </h3>

          <div>
            <label className="block text-sm font-medium text-theme-main mb-2">
              Código de país por defecto (envío individual)
            </label>
            <input
              type="text"
              placeholder="Ej: +51, +54, +1"
              className="w-full px-3 py-2 border border-theme rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              value={localConfig.defaultCountryCode || ''}
              onChange={(e) => setLocalConfig({ ...localConfig, defaultCountryCode: e.target.value })}
            />
            <p className="text-xs text-theme-muted mt-2">
              Este código se usará solo en el formulario de envío individual como valor por defecto para el país.
            </p>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-theme-main mb-2 flex items-center gap-2">
              <Clock size={16} className="text-theme-muted" />
              Retraso entre Mensajes (segundos)
            </label>
            <input
              type="number"
              value={localConfig.messageDelay ?? ''}
              onChange={(e) => {
                const value = e.target.value === '' ? undefined : Number(e.target.value);
                setLocalConfig({ ...localConfig, messageDelay: value });
              }}
              className="w-full border border-theme rounded-lg px-4 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-theme-muted mt-2">
              Tiempo aleatorio entre mensajes (entre 1 y este valor en segundos) para evitar baneos de WhatsApp. Recomendado: 2-5 segundos.
            </p>
          </div>
        </div>

        {/* Respaldo y Restauración */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
          <h3 className="text-lg font-bold text-theme-main mb-6 flex items-center gap-2">
            <Database size={20} className="text-purple-500" /> Respaldo y Restauración
          </h3>

          <div className="space-y-4">
            <p className="text-sm text-theme-muted mb-4">
              Exporta o importa tu configuración completa incluyendo menús interactivos, reglas de auto-respuesta y todos los archivos multimedia en un solo archivo ZIP.
            </p>

            {/* Export Button */}
            <div>
              <button
                onClick={handleExportComplete}
                disabled={isExporting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={18} />
                {isExporting ? 'Exportando...' : 'Exportar Configuración Completa'}
              </button>
              <p className="text-xs text-theme-muted mt-2">
                Descarga un archivo ZIP con todos tus menús, reglas y archivos multimedia
              </p>
            </div>

            {/* Import Section */}
            <div className="border-t border-theme pt-4">
              <label className="block text-sm font-medium text-theme-main mb-3">
                Importar Configuración Completa
              </label>

              <div className="space-y-3">
                <div className="relative">
                  <input
                    id="config-import-file"
                    type="file"
                    accept=".zip"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <label
                    htmlFor="config-import-file"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-theme-card border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all"
                  >
                    <Upload size={20} className="text-purple-600" />
                    <span className="text-sm font-medium text-theme-main">
                      {importFile ? importFile.name : 'Seleccionar archivo ZIP'}
                    </span>
                  </label>
                </div>

                {importFile && (
                  <div className="flex items-center gap-2 text-xs text-primary-600 bg-primary-50 px-3 py-2 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Archivo listo para importar</span>
                  </div>
                )}

                <button
                  onClick={handleImportComplete}
                  disabled={!importFile || isImporting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  <Upload size={18} />
                  {isImporting ? 'Importando configuración...' : 'Importar Configuración'}
                </button>
              </div>

              <p className="text-xs text-theme-muted mt-3">
                Sube un archivo ZIP exportado previamente. Se importarán menús, reglas y archivos multimedia automáticamente en el orden correcto.
              </p>

              {/* Global Sessions Indicator */}
              <div className="mt-3 p-3 bg-theme-base rounded-lg border border-theme">
                <p className="text-xs text-theme-muted flex items-center gap-2">
                  <strong>Ámbito de importación:</strong>
                  {globalSessionsEnabled ? (
                    <span className="flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
                        <path d="M2 12h20"></path>
                      </svg>
                      Todas las sesiones
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary-600">
                        <rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect>
                        <path d="M12 18h.01"></path>
                      </svg>
                      Solo sesión actual
                    </span>
                  )}
                </p>
                <p className="text-xs text-theme-muted mt-1">
                  {globalSessionsEnabled
                    ? 'La configuración se aplicará a todas las sesiones de WhatsApp conectadas'
                    : 'La configuración se aplicará solo a la sesión actualmente seleccionada'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Limpieza de Archivos Multimedia */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
          <h3 className="text-lg font-bold text-theme-main mb-6 flex items-center gap-2">
            <Trash2 size={20} className="text-orange-500" /> Limpieza de Archivos Multimedia
          </h3>

          <div className="space-y-4">
            <p className="text-sm text-theme-muted">
              Elimina archivos multimedia que ya no están siendo utilizados por ningún menú o regla de auto-respuesta.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => handleCleanupClick(false)}
                disabled={isCleaning}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Trash2 size={18} />
                {isCleaning ? 'Limpiando...' : 'Limpiar Archivos de la Sesión Actual'}
              </button>

              <button
                onClick={() => handleCleanupClick(true)}
                disabled={isCleaning}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Trash2 size={18} />
                {isCleaning ? 'Limpiando...' : 'Limpiar Archivos de Todas las Sesiones'}
              </button>

              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 flex-shrink-0 mt-0.5">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                  <path d="M12 9v4"></path>
                  <path d="M12 17h.01"></path>
                </svg>
                <p className="text-xs text-amber-700">
                  Esta acción eliminará permanentemente los archivos que no estén referenciados. Se recomienda exportar una copia de seguridad antes de limpiar.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Optimización de Archivos Multimedia */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
          <h3 className="text-lg font-bold text-theme-main mb-6 flex items-center gap-2">
            <Image size={20} className="text-emerald-500" /> Optimización de Archivos Multimedia
          </h3>

          <div className="space-y-4">
            <p className="text-sm text-theme-muted">
              Comprime y optimiza en el servidor todas las imágenes (JPEG, PNG, WebP, GIF) de menús y reglas para ahorrar espacio en disco y acelerar las copias de seguridad.
            </p>

            <div className="space-y-3">
              <button
                type="button"
                onClick={handleOptimizeExistingMedia}
                disabled={isOptimizing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Image size={18} />
                {isOptimizing ? 'Optimizando imágenes...' : 'Optimizar Imágenes del Servidor'}
              </button>

              <div className="flex items-start gap-2 p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5">
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  Esta acción analizará todas las imágenes almacenadas y las optimizará en su lugar sin cambiar sus nombres ni enlaces, manteniendo la integridad del bot.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Límites de envío masivo */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
          <h3 className="text-lg font-bold text-theme-main mb-6 flex items-center gap-2">
            <Users size={20} className="text-purple-500" /> Límites de Envío Masivo
          </h3>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-theme-main mb-2">
                Máximo de Contactos por Lote
              </label>
              <input
                type="number"
                autoComplete="off"
                value={localConfig.maxContactsPerBatch ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : Number(e.target.value);
                  setLocalConfig({ ...localConfig, maxContactsPerBatch: value });
                }}
                className="w-full border border-theme rounded-lg px-4 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-theme-muted mt-2">
                Número máximo de contactos que se enviarán en cada lote antes de esperar.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-main mb-2 flex items-center gap-2">
                <Timer size={16} className="text-theme-muted" />
                Tiempo de Espera entre Lotes (minutos)
              </label>
              <input
                type="number"
                autoComplete="off"
                value={localConfig.waitTimeBetweenBatches ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : Number(e.target.value);
                  setLocalConfig({ ...localConfig, waitTimeBetweenBatches: value });
                }}
                className="w-full border border-theme rounded-lg px-4 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-theme-muted mt-2">
                Tiempo de espera en minutos entre cada lote de envíos. Ejemplo: Si tienes 1000 contactos, máximo 50 por lote y 15 minutos de espera, enviará 50, esperará 15 minutos, enviará otros 50, y así sucesivamente.
              </p>
            </div>
          </div>
        </div>

        {/* Cuenta y seguridad */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
          <h3 className="text-lg font-bold text-theme-main mb-6 flex items-center gap-2">
            <Users size={20} className="text-red-500" /> Cuenta y Seguridad
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-theme-main mb-1">Correo</label>
              <input
                type="email"
                value={currentUser?.email || ''}
                disabled
                className="w-full px-3 py-2 border border-theme rounded-lg bg-theme-base text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-main mb-1">Contraseña actual</label>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 border border-theme rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-main mb-1">Nueva contraseña</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-theme rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-main mb-1">Confirmar contraseña</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-theme rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="mt-2 inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {changingPassword ? 'Cambiando contraseña...' : 'Cambiar contraseña'}
            </button>
          </div>
        </div>

        {/* Configuración de Integración de API (Solo para Platino y Administradores) */}
        {((currentUser?.subscription_type || '').toString().toLowerCase() === 'platino' || 
          (currentUser?.subscription_type || '').toString().toLowerCase() === 'administrador' || 
          currentUser?.role === 'administrador' || 
          currentUser?.email === 'daviex14@gmail.com') && (
          <ApiIntegrationSettings
            localConfig={localConfig}
            setLocalConfig={setLocalConfig}
            currentUser={currentUser}
            sessions={sessions}
            toast={toast}
          />
        )}

        {/* Proveedores de IA y Failover */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme md:col-span-2">
          <h3 className="text-lg font-bold text-theme-main mb-4 flex items-center gap-2">
            <Cpu size={20} className="text-purple-600 animate-pulse" /> Proveedores de Inteligencia Artificial & Failover Personalizado
          </h3>

          <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900 rounded-xl mb-6">
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-2.5">
                <Sparkles className="text-purple-600 shrink-0" size={20} />
                <div>
                  <h4 className="text-sm font-semibold text-purple-950 dark:text-purple-300">Failover Inteligente Activo</h4>
                  <p className="text-xs text-purple-800 dark:text-purple-400">
                    Si un proveedor agota su cuota o excede el límite por minuto, el bot salta automáticamente al siguiente proveedor configurado.
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={localConfig.aiFailoverEnabled || false}
                  onChange={(e) => setLocalConfig({ ...localConfig, aiFailoverEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-theme-card after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>
            <p className="text-[11px] text-purple-700/80 dark:text-purple-400/80 leading-relaxed">
              💡 **Llaves de API Personales**: Si dejas en blanco un proveedor de IA, el bot utilizará de forma segura las llaves globales del servidor. Ingresa tu propia API Key para sobrescribir y usar tu cuenta privada.
            </p>
            <p className="mt-2 text-[11px] text-purple-700/80 dark:text-purple-400/80 leading-relaxed">
              Proveedores habilitados: <strong>Gemini, ChatGPT, Claude y Grok</strong>. Los proveedores antiguos no participan en el failover ni se pueden configurar desde este panel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderKeyListInputs(
              "🟢 OpenAI (ChatGPT)",
              openaiKeys,
              setOpenaiKeys,
              "openai",
              "https://platform.openai.com/api-keys",
              "Usar llave global del servidor (o ingresa sk-...)",
              <>Modelos: <code>gpt-4.1-nano</code> (menor costo), <code>gpt-4.1-mini</code>.</>
            )}

            {renderKeyListInputs(
              "🔵 Google AI Studio (Gemini)",
              googleKeys,
              setGoogleKeys,
              "google",
              "https://aistudio.google.com/app/apikey",
              "Usar llave global del servidor (o ingresa AIzaSy...)",
              <>Modelos: <code>gemini-3.5-flash-lite</code> (más reciente), <code>gemini-3.1-flash-lite</code>.</>
            )}

            {renderKeyListInputs(
              "🟠 Anthropic (Claude)",
              anthropicKeys,
              setAnthropicKeys,
              "anthropic",
              "https://console.anthropic.com/settings/keys",
              "Usar llave global del servidor (o ingresa sk-ant-...)",
              <>Modelos: <code>claude-haiku-4-5</code> (menor costo), <code>claude-sonnet-5</code>.</>
            )}

            {renderKeyListInputs(
              "⚫ xAI (Grok)",
              xaiKeys,
              setXaiKeys,
              "xai",
              "https://console.x.ai/",
              "Usar llave global del servidor (o ingresa xai-...)",
              <>Modelos: <code>grok-4.3</code> (menor costo), <code>grok-4.6</code>.</>
            )}

          </div>

          </div>
        </div>

        {/* Registro de fallos de IA */}
        <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme md:col-span-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-red-500/10 p-2.5 text-red-500">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-theme-main">Eventos fallidos de IA</h3>
                <p className="mt-1 text-xs text-theme-muted">
                  Sólo se muestran llamadas que fallaron. Se guarda el proveedor, modelo, clave identificada de forma parcial y el motivo; nunca el prompt ni la clave completa.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void loadAIFailureLogs()}
              disabled={loadingAIFailures}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-theme px-3 py-2 text-xs font-semibold text-theme-main transition-colors hover:bg-theme-base disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw size={14} className={loadingAIFailures ? 'animate-spin' : ''} />
              Actualizar
            </button>
          </div>

          <div className="mt-5 overflow-hidden rounded-xl border border-theme">
            {loadingAIFailures ? (
              <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-theme-muted">
                <RefreshCw size={16} className="animate-spin" /> Cargando fallos registrados...
              </div>
            ) : aiFailures.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <CheckCircle size={28} className="mx-auto mb-2 text-emerald-500" />
                <p className="text-sm font-semibold text-theme-main">No hay fallos de IA registrados</p>
                <p className="mt-1 text-xs text-theme-muted">Los nuevos errores aparecerán aquí después de actualizar.</p>
              </div>
            ) : (
              <div className="max-h-[28rem] divide-y divide-theme overflow-y-auto">
                {aiFailures.map((failure) => (
                  <article key={failure.id} className="space-y-2 p-4 transition-colors hover:bg-theme-base/60">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="rounded-md bg-red-500/10 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-red-600 dark:text-red-400">
                          Falló
                        </span>
                        <span className="text-sm font-bold text-theme-main">{failure.provider}</span>
                        <span className="max-w-full break-all rounded bg-theme-base px-2 py-1 font-mono text-[11px] text-theme-muted">
                          Modelo: {failure.model || 'modelo no indicado'}
                        </span>
                        {failure.status_code > 0 && (
                          <span className="rounded bg-amber-500/10 px-2 py-1 font-mono text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                            HTTP {failure.status_code}
                          </span>
                        )}
                      </div>
                      <time className="shrink-0 text-[11px] text-theme-muted" dateTime={failure.created_at}>
                        {formatAIFailureDate(failure.created_at)}
                      </time>
                    </div>

                    <p className="max-h-28 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-red-500/[0.04] px-3 py-2 font-mono text-[11px] leading-relaxed text-theme-main">
                      {failure.reason}
                    </p>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-theme-muted">
                      <span>API: {failure.provider}{failure.api_key_index > 0 ? ` · clave #${failure.api_key_index}` : ''}{failure.api_key_hint ? ` (${failure.api_key_hint})` : ''}</span>
                      {failure.session_id && <span>Sesión: {failure.session_id}</span>}
                      {failure.contact_jid && <span>Contacto: {failure.contact_jid}</span>}
                      {failure.source && <span>Origen: {failure.source}</span>}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-8 py-3 rounded-lg font-medium hover:bg-black dark:hover:bg-white flex items-center gap-2 shadow-lg transition-all duration-200"
        >
          <Save size={18} /> Guardar Configuración
        </button>
      </div>

      {/* Modal de Confirmación de Limpieza */}
      <ConfirmModal
        isOpen={showCleanupConfirm}
        onClose={() => setShowCleanupConfirm(false)}
        onConfirm={confirmCleanup}
        title="Confirmar Limpieza de Archivos"
        message={
          cleanupAllSessions
            ? '¿Estás seguro de que deseas eliminar todos los archivos multimedia huérfanos de TODAS las sesiones? Esta acción no se puede deshacer.'
            : '¿Estás seguro de que deseas eliminar todos los archivos multimedia huérfanos de la sesión actual? Esta acción no se puede deshacer.'
        }
        confirmText="Sí, Eliminar"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};
